import { useConfirmDialog } from '@/composables/useConfirmDialog'

import { tokenService } from '@/utils/auth/tokenService'
import { eventEmitter } from '@/utils/events/event-emitter'

import { feCompaniesAPI } from '@/APIs'
import { REDIRECT_ERROR, ERROR_MESSAGE_CONFIG, type RedirectErrorType } from '@/constant/client/authErrors'
import { CommonEvent } from '@/constant/events'
import { LOCAL_STORAGE_KEYS } from '@/constant/storage'

import type { Router } from 'vue-router'

interface CompanyAndOrgIdsResult {
  companyUuid: string | null
  orgId: string | null
  error?: RedirectErrorType
}

/**
 * 獲取公司和組織 ID（共用邏輯）
 * 1. 檢查 localStorage 是否有儲存公司 ID 及組織 ID
 * 2. 若沒有，獲取公司列表，遍歷所有公司找到第一個有組織的公司
 * 3. 儲存到 localStorage
 * @returns 公司 UUID 和組織 ID，如果發生錯誤則回傳 null 和錯誤類型
 */
export const getCompanyAndOrgIds = async (): Promise<CompanyAndOrgIdsResult> => {
  let companyUuid = localStorage.getItem(LOCAL_STORAGE_KEYS.COMPANY_UUID)
  let orgId = localStorage.getItem(LOCAL_STORAGE_KEYS.ORG_ID)

  if (!companyUuid || !orgId) {
    try {
      const { data } = await feCompaniesAPI.getCompaniesAndOrganizations()

      if (data?.companyList && data.companyList.length > 0) {
        for (const company of data.companyList) {
          const currentCompanyUuid = company.companyUuid || ''
          const companyOrgs = data.organizationList?.filter((org) => org.companyUuid === currentCompanyUuid) || []
          if (companyOrgs.length > 0) {
            companyUuid = currentCompanyUuid
            orgId = companyOrgs[0].companyOrganizationUuid || ''
            break
          }
        }

        if (!companyUuid || !orgId) {
          return { companyUuid: null, orgId: null, error: REDIRECT_ERROR.NO_ORGANIZATION }
        }

        localStorage.setItem(LOCAL_STORAGE_KEYS.COMPANY_UUID, companyUuid)
        localStorage.setItem(LOCAL_STORAGE_KEYS.ORG_ID, orgId)
      } else {
        return { companyUuid: null, orgId: null, error: REDIRECT_ERROR.NO_PERMISSION }
      }
    } catch (error) {
      console.error('Failed to get companies and organizations:', error)
      return { companyUuid: null, orgId: null, error: REDIRECT_ERROR.FETCH_FAILED }
    }
  }

  return { companyUuid, orgId }
}

/**
 * 登出並清除所有存儲
 * 清除所有 token 和 localStorage，並重新載入頁面
 * @param closeDialog 是否關閉對話框（預設為 true）
 */
export const logoutAndClearStorage = (closeDialog = true) => {
  tokenService.clearTokens()
  localStorage.clear()

  if (closeDialog) {
    eventEmitter.emit(CommonEvent.CLOSE_DIALOG)
  }

  window.location.href = '/login'
}

/**
 * 顯示錯誤對話框並在確認後執行回調
 * @param title 對話框標題
 * @param text 錯誤訊息
 * @param onConfirm 確認回調函式（預設為登出並清除存儲）
 */
export const showAuthErrorDialog = (title: string, text: string, onConfirm: () => void = logoutAndClearStorage) => {
  const { alert } = useConfirmDialog()
  alert({
    title,
    text,
    type: 'danger',
    confirmButtonText: '確定',
    dismissible: false,
    onConfirm
  })
}

/**
 * 顯示重定向錯誤訊息
 * @param error 錯誤類型
 */
export const showRedirectError = (error: RedirectErrorType) => {
  const config = ERROR_MESSAGE_CONFIG[error]

  if (!config) {
    return
  }

  // 先清除記憶體 token，否則重導回 login 時守衛會因 token 仍存在而重複檢查、重複彈窗，形成無限閃爍
  tokenService.clearTokens()

  // FETCH_FAILED 屬暫時性錯誤，可重新載入重試（OIDC session 仍在，token 會自動還原）；
  // 但 API 持續失敗時，只能重試會形成無限彈窗迴圈，需提供登出出口
  if (error === REDIRECT_ERROR.FETCH_FAILED) {
    const { confirm } = useConfirmDialog()
    confirm({
      title: config.title,
      text: config.text,
      type: 'danger',
      confirmButtonText: '重新載入',
      cancelButtonText: '登出',
      dismissible: false,
      onConfirm: () => window.location.reload(),
      onCancel: () => logoutAndClearStorage(false)
    })
    return
  }

  showAuthErrorDialog(config.title, config.text)
}

/**
 * 取得重定向到前台的路由物件（用於 router guards）
 * @returns 路由物件，用於 router.beforeEach 中返回
 */
export const getRedirectToFrontendRoute = async () => {
  const result = await getCompanyAndOrgIds()

  if (result.error) {
    showRedirectError(result.error)
    return { name: 'login' as const }
  }

  if (!result.companyUuid || !result.orgId) {
    console.error('Invalid company or organization ID')
    return { name: 'login' as const }
  }

  return {
    name: 'shorter-list' as const,
    query: { companyUuid: result.companyUuid, orgId: result.orgId }
  }
}

/**
 * 重定向到前台頁面（用於組件中）
 * @param router Vue Router 實例
 */
export const redirectToFrontend = async (router: Router) => {
  const result = await getCompanyAndOrgIds()

  if (result.error) {
    showRedirectError(result.error)
    return
  }

  if (!result.companyUuid || !result.orgId) {
    console.error('Invalid company or organization ID')
    logoutAndClearStorage()
    return
  }

  // 跳轉到前台頁面
  router.push({
    name: 'shorter-list',
    query: { companyUuid: result.companyUuid, orgId: result.orgId }
  })
}
